// src/pages/Landing.jsx
import React, { useState, useEffect, useRef } from 'react';
import heroImage from '../data/hero.jpg';
import abtMe from '../data/abt-me.jpeg';
import { submitAppointmentRequest } from '../services/appointments';

const Landing = () => {
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [visibleElements, setVisibleElements] = useState(new Set());
  const heroTitleRef = useRef(null);
  
  // Intersection Observer for animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setVisibleElements(prev => new Set([...prev, entry.target.dataset.animateId]));
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    const elements = document.querySelectorAll('[data-animate-id]');
    elements.forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  // Typing effect for hero title
  useEffect(() => {
    const title = "Diyetisyen Kerem Barbak";
    const element = heroTitleRef.current;
    if (!element) return;
    
    let i = 0;
    element.textContent = '';
    const typeTimer = setInterval(() => {
      if (i < title.length) {
        element.textContent += title.charAt(i);
        i++;
      } else {
        clearInterval(typeTimer);
      }
    }, 150);

    return () => clearInterval(typeTimer);
  }, []);

  // Smooth scroll
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    element?.scrollIntoView({ behavior: 'smooth' });
  };

  // Form handling
  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    setSubmitError('');
    setIsFormSubmitted(false);
    setIsSubmitting(true);

    const result = await submitAppointmentRequest(formData, { honeypot });

    setIsSubmitting(false);

    if (!result.ok) {
      setSubmitError(
        result.reason === 'rate-limited'
          ? 'Az önce bir talep gönderdiniz. Lütfen bir dakika bekleyip tekrar deneyin.'
          : 'Talebiniz gönderilemedi. Lütfen doğrudan dyt.kerembarbak@gmail.com adresinden bize ulaşın.'
      );
      return;
    }

    setIsFormSubmitted(true);
    setFormData({
      name: '',
      email: '',
      phone: '',
      service: '',
      message: ''
    });
    setHoneypot('');
  };

  // Floating animation component
  const FloatingElement = ({ emoji, index }) => {
    const [position, setPosition] = useState({ x: 0, y: 0, rotation: 0 });
    
    useEffect(() => {
      const interval = setInterval(() => {
        const time = Date.now() / 1000;
        setPosition({
          x: Math.cos(time / 1.5 + index) * 15,
          y: Math.sin(time + index) * 20,
          rotation: Math.sin(time / 2 + index) * 10
        });
      }, 50);
      
      return () => clearInterval(interval);
    }, [index]);

    const basePositions = [
      { top: '15%', left: '8%' },
      { top: '25%', right: '12%' },
      { top: '60%', left: '5%' },
      { top: '70%', right: '8%' },
      { top: '35%', left: '15%' },
      { top: '80%', left: '25%' },
      { top: '45%', right: '25%' },
      { top: '10%', left: '40%' },
      { top: '85%', right: '40%' },
      { top: '55%', right: '5%' }
    ];

    return (
      <div
        className="floating-element"
        style={{
          ...basePositions[index % basePositions.length],
          transform: `translate(${position.x}px, ${position.y}px) rotate(${position.rotation}deg)`
        }}
      >
        {emoji}
      </div>
    );
  };

  const floatingEmojis = ['🥗', '🏃‍♀️', '💚', '🍎', '⚖️', '🥑', '💪', '🥕', '🌟', '❤️'];

  return (
    <div className="dietician-website">
      <style>{`
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        :root {
          --primary: #2563eb;
          --primary-dark: #1e40af;
          --secondary: #0891b2;
          --accent: #dc2626;
          --text-dark: #0f172a;
          --text-light: #475569;
          --white: #ffffff;
          --bg-light: #1e293b;
          --bg-dark: #0f172a;
          --gradient-primary: linear-gradient(135deg, #1e293b 0%, #334155 100%);
          --gradient-secondary: linear-gradient(135deg, #7c3aed 0%, #3b82f6 100%);
          --gradient-success: linear-gradient(135deg, #059669 0%, #0891b2 100%);
          --gradient-dark: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
        }

        html {
          scroll-behavior: smooth;
          height: 100%;
          overflow-x: hidden;
          overflow-y: auto;
        }

        body {
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
          line-height: 1.6;
          color: var(--text-dark);
          margin: 0;
          padding: 0;
          width: 100%;
          min-height: 100%;
          overflow-x: hidden;
          overflow-y: auto;
          position: relative;
        }

        .dietician-website {
          width: 100%;
          min-height: 100vh;
          overflow-x: hidden;
          position: relative;
        }

        .navbar {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          width: 100%;
          background: rgba(15, 23, 42, 0.95);
          backdrop-filter: blur(10px);
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
          z-index: 1000;
          padding: 1rem 0;
          transition: all 0.3s ease;
        }

        .nav-container {
          width: 100%;
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 1rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .nav-logo {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 1.2rem;
          font-weight: 700;
          color: var(--white);
          white-space: nowrap;
        }

        .nav-logo span:first-child {
          font-size: 1.5rem;
        }

        .hero {
          min-height: 100vh;
          background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
          position: relative;
          display: flex;
          align-items: center;
          overflow: hidden;
          width: 100%;
        }

        .hero::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grain" width="100" height="100" patternUnits="userSpaceOnUse"><circle cx="20" cy="20" r="1" fill="rgba(255,255,255,0.05)"/><circle cx="80" cy="40" r="1.5" fill="rgba(255,255,255,0.03)"/><circle cx="40" cy="80" r="1" fill="rgba(255,255,255,0.04)"/></pattern></defs><rect width="100" height="100" fill="url(%23grain)"/></svg>');
          animation: float 20s ease-in-out infinite;
        }

        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(1deg); }
        }

        .hero-container {
          width: 100%;
          max-width: 1200px;
          margin: 0 auto;
          padding: 80px 1rem 0 1rem;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 2rem;
          align-items: center;
          z-index: 2;
          position: relative;
          min-height: 100vh;
        }

        .hero-content {
          animation: slideInLeft 1s ease-out;
          min-width: 0;
          width: 100%;
        }

        @keyframes slideInLeft {
          from { transform: translateX(-100px); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }

        .hero-title {
          font-size: 3.5rem;
          font-weight: 700;
          color: var(--white);
          margin-bottom: 1rem;
          line-height: 1.2;
          min-height: 4.2rem;
          word-wrap: break-word;
        }

        .hero-subtitle {
          font-size: 1.5rem;
          color: rgba(255, 255, 255, 0.9);
          margin-bottom: 1rem;
          font-weight: 300;
        }

        .hero-description {
          font-size: 1.1rem;
          color: rgba(255, 255, 255, 0.8);
          margin-bottom: 2rem;
          line-height: 1.8;
        }

        .hero-buttons {
          display: flex;
          gap: 1rem;
          flex-wrap: wrap;
        }

        .btn {
          padding: 1rem 2rem;
          border: none;
          border-radius: 50px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          text-decoration: none;
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          position: relative;
          overflow: hidden;
          background: transparent;
          white-space: nowrap;
        }

        .btn::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
          transition: left 0.5s;
        }

        .btn:hover::before {
          left: 100%;
        }

        .btn-primary {
          background: var(--white);
          color: var(--primary);
          box-shadow: 0 10px 30px rgba(0,0,0,0.2);
        }

        .btn-primary:hover {
          transform: translateY(-3px);
          box-shadow: 0 15px 40px rgba(0,0,0,0.3);
        }

        .btn-outline {
          background: transparent;
          color: var(--white);
          border: 2px solid var(--white);
        }

        .btn-outline:hover {
          background: var(--white);
          color: var(--primary);
          transform: translateY(-3px);
        }

        .hero-image {
          position: relative;
          animation: slideInRight 1s ease-out;
          display: flex;
          justify-content: center;
          align-items: center;
          min-width: 0;
          width: 100%;
        }

        @keyframes slideInRight {
          from { transform: translateX(100px); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }

        .profile-image {
          width: 400px;
          height: 400px;
          max-width: 100%;
          border-radius: 50%;
          object-fit: cover;
          border: 8px solid rgba(255, 255, 255, 0.2);
          box-shadow: 0 20px 60px rgba(0,0,0,0.3);
          position: relative;
          z-index: 2;
          animation: gentle-bounce 6s ease-in-out infinite;
        }

        @keyframes gentle-bounce {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }

        .profile-decoration {
          position: absolute;
          width: 450px;
          height: 450px;
          max-width: calc(100% + 50px);
          border: 2px dashed rgba(255, 255, 255, 0.3);
          border-radius: 50%;
          top: -25px;
          left: -25px;
          animation: rotate 30s linear infinite;
        }

        @keyframes rotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .floating-element {
          position: absolute;
          font-size: 4rem;
          opacity: 0.8;
          z-index: 1;
          transition: transform 0.1s ease;
          pointer-events: none;
        }

        .section {
          padding: 6rem 0;
          position: relative;
          width: 100%;
        }

        .about {
          background: var(--bg-dark);
          color: var(--white);
        }

        .container {
          width: 100%;
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 1rem;
        }

        .section-title {
          text-align: center;
          font-size: 2.5rem;
          font-weight: 700;
          color: var(--white);
          margin-bottom: 1rem;
          position: relative;
          word-wrap: break-word;
        }

        .section-title::after {
          content: '';
          position: absolute;
          bottom: -10px;
          left: 50%;
          transform: translateX(-50%);
          width: 60px;
          height: 4px;
          background: var(--gradient-secondary);
          border-radius: 2px;
        }

        .section-subtitle {
          text-align: center;
          font-size: 1.2rem;
          color: rgba(255, 255, 255, 0.7);
          margin-bottom: 4rem;
        }

        .about-content {
          display: grid;
          grid-template-columns: 1fr 2fr;
          gap: 4rem;
          align-items: center;
          width: 100%;
        }

        .about-image {
          position: relative;
          width: 100%;
        }

        .about-img {
          width: 100%;
          max-width: 100%;
          border-radius: 20px;
          box-shadow: 0 20px 60px rgba(0,0,0,0.1);
          transition: transform 0.3s ease;
        }

        .about-img:hover {
          transform: scale(1.05);
        }

        .about-text {
          font-size: 1.1rem;
          line-height: 1.8;
          color: rgba(255, 255, 255, 0.8);
          margin-bottom: 2rem;
        }

        .expertise-list {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1rem;
          margin-top: 2rem;
          width: 100%;
        }

        .expertise-item {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 10px;
          box-shadow: 0 5px 15px rgba(0,0,0,0.2);
          transition: transform 0.3s ease;
          color: var(--white);
          word-wrap: break-word;
        }

        .expertise-item:hover {
          transform: translateY(-5px);
        }

        .services {
          background: var(--bg-light);
        }

        .services .section-title {
          color: var(--white);
        }

        .services .section-subtitle {
          color: rgba(255, 255, 255, 0.7);
        }

        .services-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 2rem;
          margin-top: 3rem;
          width: 100%;
        }

        .service-card {
          background: rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.2);
          padding: 2rem;
          border-radius: 20px;
          box-shadow: 0 10px 30px rgba(0,0,0,0.3);
          text-align: center;
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
          color: var(--white);
          width: 100%;
          min-width: 0;
        }

        .service-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: var(--gradient-secondary);
          transform: scaleX(0);
          transition: transform 0.3s ease;
        }

        .service-card:hover::before {
          transform: scaleX(1);
        }

        .service-card:hover {
          transform: translateY(-10px);
          box-shadow: 0 20px 50px rgba(0,0,0,0.15);
        }

        .service-icon {
          font-size: 3rem;
          margin-bottom: 1rem;
          display: block;
        }

        .service-card h3 {
          font-size: 1.5rem;
          font-weight: 600;
          color: var(--white);
          margin-bottom: 1rem;
          word-wrap: break-word;
        }

        .service-card p {
          color: rgba(255, 255, 255, 0.8);
          line-height: 1.6;
        }

        .testimonials {
          background: var(--gradient-dark);
          color: var(--white);
        }

        .testimonials-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
          gap: 2rem;
          margin-top: 3rem;
          width: 100%;
        }

        .testimonial-card {
          background: rgba(255, 255, 255, 0.1);
          padding: 2rem;
          border-radius: 20px;
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.2);
          transition: transform 0.3s ease;
          width: 100%;
          min-width: 0;
        }

        .testimonial-card:hover {
          transform: translateY(-5px);
        }

        .testimonial-text {
          font-size: 1.1rem;
          line-height: 1.6;
          margin-bottom: 1.5rem;
          font-style: italic;
        }

        .testimonial-author {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .author-avatar {
          width: 50px;
          height: 50px;
          border-radius: 50%;
          object-fit: cover;
          flex-shrink: 0;
        }

        .author-info h4 {
          font-weight: 600;
          margin-bottom: 0.25rem;
          word-wrap: break-word;
        }

        .author-info span {
          opacity: 0.8;
          font-size: 0.9rem;
        }

        .contact {
          background: var(--bg-light);
        }

        .contact .section-title {
          color: var(--white);
        }

        .contact .section-subtitle {
          color: rgba(255, 255, 255, 0.7);
        }

        .contact-content {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 4rem;
          margin-top: 3rem;
          width: 100%;
        }

        .contact-info h3 {
          font-size: 1.5rem;
          color: var(--white);
          margin-bottom: 1rem;
        }

        .contact-item {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin-bottom: 1.5rem;
          padding: 1rem;
          background: rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 10px;
          transition: transform 0.3s ease;
          color: var(--white);
          width: 100%;
        }

        .contact-item:hover {
          transform: translateX(10px);
        }

        .contact-icon {
          width: 50px;
          height: 50px;
          background: var(--gradient-primary);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--white);
          font-size: 1.2rem;
          flex-shrink: 0;
        }

        .contact-form {
          background: rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.2);
          padding: 2rem;
          border-radius: 20px;
          color: var(--white);
          width: 100%;
        }

        .contact-form h3 {
          color: var(--white);
          margin-bottom: 1.5rem;
        }

        .form-group {
          margin-bottom: 1.5rem;
          width: 100%;
        }

        .form-group label {
          display: block;
          margin-bottom: 0.5rem;
          font-weight: 600;
          color: var(--white);
        }

        .form-group input,
        .form-group textarea,
        .form-group select {
          width: 100%;
          max-width: 100%;
          padding: 1rem;
          border: 2px solid rgba(255, 255, 255, 0.2);
          border-radius: 10px;
          background: rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(10px);
          color: #000;
          font-size: 1rem;
          transition: all 0.3s ease;
          box-sizing: border-box;
        }

        .form-group input::placeholder,
        .form-group textarea::placeholder {
          color: rgba(0, 0, 0, 0.6);
        }

        .form-group input:focus,
        .form-group textarea:focus,
        .form-group select:focus {
          outline: none;
          border-color: var(--primary);
          box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.3);
        }

        .form-group textarea {
          height: 120px;
          resize: vertical;
        }

        .footer {
          background: var(--bg-dark);
          color: var(--white);
          text-align: center;
          padding: 2rem 0;
          width: 100%;
        }

        .fade-in {
          opacity: 0;
          transform: translateY(30px);
          transition: all 0.6s ease;
        }

        .fade-in.visible {
          opacity: 1;
          transform: translateY(0);
        }

        .success-message {
          background: #10b981;
          color: white;
          padding: 1rem;
          border-radius: 10px;
          margin-bottom: 1rem;
          text-align: center;
        }

        .error-message {
          background: #ef4444;
          color: white;
          padding: 1rem;
          border-radius: 10px;
          margin-bottom: 1rem;
          text-align: center;
        }

        /* Mobil için özel düzenlemeler - SCROLL SORUNU ÇÖZÜMÜ */
        @media (max-width: 768px) {
          html {
            height: auto;
            overflow-x: hidden;
            overflow-y: auto;
          }

          body {
            height: auto;
            min-height: 100vh;
            overflow-x: hidden;
            overflow-y: auto;
            -webkit-overflow-scrolling: touch;
          }

          .dietician-website {
            min-height: auto;
            height: auto;
            overflow-x: hidden;
            position: relative;
          }

          .nav-container {
            padding: 0 1rem;
          }

          .nav-logo {
            font-size: 1rem;
          }

          .nav-logo span:first-child {
            font-size: 1.2rem;
          }

          /* HERO SECTION - MOBİL SCROLL SORUNU ÇÖZÜMÜ */
          .hero {
            min-height: auto;
            height: auto;
            position: relative;
            overflow: hidden;
            padding: 80px 0 60px 0;
          }

          .hero-container {
            padding: 40px 1rem 40px 1rem;
            grid-template-columns: 1fr;
            text-align: center;
            gap: 2rem;
            min-height: auto;
            height: auto;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
          }

          .hero-content {
            width: 100%;
            max-width: 100%;
            order: 2;
          }

          .hero-image {
            width: 100%;
            max-width: 100%;
            order: 1;
          }

          .hero-title {
            font-size: 2rem;
            line-height: 1.1;
            min-height: auto;
            margin-bottom: 1rem;
          }

          .hero-subtitle {
            font-size: 1.2rem;
            margin-bottom: 1rem;
          }

          .hero-description {
            font-size: 1rem;
            margin-bottom: 2rem;
          }

          .profile-image {
            width: 280px;
            height: 280px;
            max-width: 90vw;
            max-height: 90vw;
          }

          .profile-decoration {
            width: 320px;
            height: 320px;
            max-width: calc(90vw + 40px);
            max-height: calc(90vw + 40px);
          }

          .floating-element {
            display: none;
          }

          .about-content {
            grid-template-columns: 1fr;
            gap: 2rem;
          }

          .contact-content {
            grid-template-columns: 1fr;
            gap: 2rem;
          }

          .section {
            padding: 4rem 0;
            position: relative;
            overflow: visible;
          }

          .container {
            padding: 0 1rem;
            width: 100%;
            max-width: 100%;
          }

          .section-title {
            font-size: 2rem;
          }

          .services-grid {
            grid-template-columns: 1fr;
          }

          .testimonials-grid {
            grid-template-columns: 1fr;
          }

          .expertise-list {
            grid-template-columns: 1fr;
          }

          .hero-buttons {
            justify-content: center;
            flex-direction: column;
            align-items: center;
            gap: 1rem;
            width: 100%;
          }

          .btn {
            padding: 0.8rem 1.5rem;
            font-size: 0.9rem;
            min-width: 200px;
            justify-content: center;
            width: auto;
            max-width: 280px;
          }
        }

        @media (max-width: 480px) {
          .hero {
            padding: 80px 0 40px 0;
          }

          .hero-container {
            padding: 20px 1rem 20px 1rem;
            gap: 1.5rem;
          }

          .hero-title {
            font-size: 1.8rem;
          }

          .hero-subtitle {
            font-size: 1.1rem;
          }

          .hero-description {
            font-size: 0.95rem;
            line-height: 1.6;
          }

          .profile-image {
            width: 240px;
            height: 240px;
            max-width: 85vw;
            max-height: 85vw;
          }

          .profile-decoration {
            width: 280px;
            height: 280px;
            max-width: calc(85vw + 40px);
            max-height: calc(85vw + 40px);
          }

          .section-title {
            font-size: 1.8rem;
          }

          .section-subtitle {
            font-size: 1rem;
          }

          .service-card,
          .testimonial-card {
            padding: 1.5rem;
          }

          .contact-form {
            padding: 1.5rem;
          }

          .btn {
            padding: 0.7rem 1.2rem;
            font-size: 0.85rem;
            min-width: 180px;
            max-width: 250px;
          }
        }

        /* Ekstra mobil scroll düzeltmeleri */
        @media (max-width: 768px) {
          .hero::before {
            position: absolute;
            height: 100%;
          }

          /* Touch scroll optimizasyonu */
          * {
            -webkit-tap-highlight-color: transparent;
          }

          /* Viewport yükseklik problemlerini çözme */
          .hero-container {
            min-height: calc(100vh - 160px);
            max-height: none;
          }

          /* Dikey scroll iyileştirmesi */
          .section {
            scroll-margin-top: 80px;
          }
        }

        /* iPhone ve diğer küçük ekranlar için */
        @media (max-width: 375px) {
          .hero {
            padding: 70px 0 30px 0;
          }

          .hero-container {
            padding: 15px 0.5rem 15px 0.5rem;
            min-height: calc(100vh - 140px);
          }

          .profile-image {
            width: 200px;
            height: 200px;
          }

          .profile-decoration {
            width: 240px;
            height: 240px;
          }

          .hero-title {
            font-size: 1.6rem;
          }

          .container {
            padding: 0 0.5rem;
          }
        }

        /* Yatay overflow önleme */
        * {
          max-width: 100%;
          box-sizing: border-box;
        }

        img {
          max-width: 100%;
          height: auto;
        }

        /* Scroll davranış iyileştirmesi */
        html {
          scroll-padding-top: 80px;
        }

        @media (max-width: 768px) {
          html {
            scroll-padding-top: 60px;
          }
        }
      `}</style>

      {/* Navigation */}
      <nav className="navbar">
        <div className="nav-container">
          <div className="nav-logo">
            <span>🥗</span>
            <span>Dyt. Kerem Barbak</span>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero">
        {floatingEmojis.map((emoji, index) => (
          <FloatingElement key={index} emoji={emoji} index={index} />
        ))}
        
        <div className="hero-container">
          <div className="hero-content">
            <h1 ref={heroTitleRef} className="hero-title"></h1>
            <p className="hero-subtitle">Sporcu Diyetisyeni</p>
            <p className="hero-description">
              Sağlıklı yaşamın ve ideal vücudun kapısını açmak için buradayım. 100'den fazla sporcu ile çalıştığım tecrübemle, sadece hedeflerinize ulaşmanızı değil, bu yolculuğu sürdürülebilir bir yaşam tarzına dönüştürmenizi sağlıyorum. Birlikte, en iyi versiyonunuzu keşfedelim.
            </p>
            <div className="hero-buttons">
              <button onClick={() => scrollToSection('contact')} className="btn btn-primary">
                <span>🗓️</span> Randevu Al
              </button>
              <button onClick={() => scrollToSection('about')} className="btn btn-outline">
                <span>👋</span> Beni Tanı
              </button>
            </div>
          </div>
          
          <div className="hero-image">
            <div className="profile-decoration"></div>
            <img src={heroImage} alt="Diyetisyen Kerem Barbak" className="profile-image" />
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="section about">
        <div className="container">
          <h2 
            className={`section-title fade-in ${visibleElements.has('about-title') ? 'visible' : ''}`}
            data-animate-id="about-title"
          >
            Hakkımda
          </h2>
          <p 
            className={`section-subtitle fade-in ${visibleElements.has('about-subtitle') ? 'visible' : ''}`}
            data-animate-id="about-subtitle"
          >
            Sağlıklı yaşamın anahtarı doğru beslenme alışkanlıklarında saklı
          </p>
          
          <div className="about-content">
            <div 
              className={`about-image fade-in ${visibleElements.has('about-image') ? 'visible' : ''}`}
              data-animate-id="about-image"
            >
              <img src={abtMe} alt="Kerem Barbak" className="profile-image" />
            </div>
            
            <div 
              className={`about-text fade-in ${visibleElements.has('about-text') ? 'visible' : ''}`}
              data-animate-id="about-text"
            >
              <p>
                Merhaba! Ben Kerem Barbak, 100+ sporcu danışan tecrübem ile beslenme ve diyet alanında faaliyet gösteren bir sporcu diyetisyeniyim. 
                İzmir Katip Çelebi Üniversitesi Beslenme ve Diyetetik bölümünden mezun olduktan sonra, yüzlerce kişinin 
                sağlıklı yaşam tarzına geçişine yardımcı oldum.
              </p>
              <p>
                Amacım, size sadece kilo vermenizi sağlamak değil, yaşam boyu sürdürebileceğiniz sağlıklı 
                beslenme alışkanlıkları kazandırmaktır. Her bireyin benzersiz olduğuna inanıyor, size özel 
                beslenme programları hazırlıyorum.
              </p>
              
              <div className="expertise-list">
                <div className="expertise-item">
                  <span>✅</span>
                  <span>Kilo Yönetimi</span>
                </div>
                <div className="expertise-item">
                  <span>✅</span>
                  <span>Sporcu Beslenmesi</span>
                </div>
                <div className="expertise-item">
                  <span>✅</span>
                  <span>Adölesan Beslenmesi</span>
                </div>
                <div className="expertise-item">
                  <span>✅</span>
                  <span>Hastalıklarda Beslenme</span>
                </div>
                <div className="expertise-item">
                  <span>✅</span>
                  <span>Fitness Danışmanlığı</span>
                </div>
                <div className="expertise-item">
                  <span>✅</span>
                  <span>Sürdürülebilir Beslenme</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="section services">
        <div className="container">
          <h2 
            className={`section-title fade-in ${visibleElements.has('services-title') ? 'visible' : ''}`}
            data-animate-id="services-title"
          >
            Hizmetlerim
          </h2>
          <p 
            className={`section-subtitle fade-in ${visibleElements.has('services-subtitle') ? 'visible' : ''}`}
            data-animate-id="services-subtitle"
          >
            Size en uygun beslenme çözümlerini sunuyorum
          </p>
          
          <div className="services-grid">
            <div 
              className={`service-card fade-in ${visibleElements.has('service1') ? 'visible' : ''}`}
              data-animate-id="service1"
            >
              <span className="service-icon">🎯</span>
              <h3>Kişisel Beslenme Danışmanlığı</h3>
              <p>Size özel hazırlanmış beslenme programları ile hedeflerinize sağlıklı bir şekilde ulaşın.</p>
            </div>
            
            <div 
              className={`service-card fade-in ${visibleElements.has('service2') ? 'visible' : ''}`}
              data-animate-id="service2"
            >
              <span className="service-icon">📱</span>
              <h3>Online Takip</h3>
              <p>Uzaktan takip sistemi ile her an yanınızdayım. WhatsApp desteği ve düzenli kontroller.</p>
            </div>
            
            <div 
              className={`service-card fade-in ${visibleElements.has('service3') ? 'visible' : ''}`}
              data-animate-id="service3"
            >
              <span className="service-icon">🏃‍♀️</span>
              <h3>Spor Beslenmesi</h3>
              <p>Atletik performansınızı artırmak için spor öncesi, sırası ve sonrası beslenme programları.</p>
            </div>
            
            <div 
              className={`service-card fade-in ${visibleElements.has('service4') ? 'visible' : ''}`}
              data-animate-id="service4"
            >
              <span className="service-icon">👦👧</span>
              <h3>Adölesan Beslenmesi</h3>
              <p>Ergenlikten yetişkinlik dönemine geçiş evresinde uzman beslenme desteği.</p>
            </div>
            
            <div 
              className={`service-card fade-in ${visibleElements.has('service5') ? 'visible' : ''}`}
              data-animate-id="service5"
            >
              <span className="service-icon">🏥</span>
              <h3>Medikal Beslenme</h3>
              <p>Diyabet, hipertansiyon, kolesterol gibi hastalıklarda özel beslenme protokolleri.</p>
            </div>
            
            <div 
              className={`service-card fade-in ${visibleElements.has('service6') ? 'visible' : ''}`}
              data-animate-id="service6"
            >
              <span className="service-icon">👥</span>
              <h3>Grup Eğitimleri</h3>
              <p>Şirketler ve topluluklar için beslenme eğitimleri ve seminerler.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="section testimonials">
        <div className="container">
          <h2 
            className={`section-title fade-in ${visibleElements.has('testimonials-title') ? 'visible' : ''}`}
            data-animate-id="testimonials-title"
          >
            Danışan Görüşleri
          </h2>
          <p 
            className={`section-subtitle fade-in ${visibleElements.has('testimonials-subtitle') ? 'visible' : ''}`}
            data-animate-id="testimonials-subtitle"
          >
            Başarı hikayeleri beni motive ediyor
          </p>
          
          <div className="testimonials-grid">
            <div 
              className={`testimonial-card fade-in ${visibleElements.has('testimonial1') ? 'visible' : ''}`}
              data-animate-id="testimonial1"
            >
              <p className="testimonial-text">
                "Kerem Bey ile çalışmaya başladıktan sonra sadece kilo vermedim, yaşam tarzımı tamamen değiştirdim. 
                40 günde 16 kilo verdim ve artık çok daha enerjik hissediyorum!"
              </p>
              <div className="testimonial-author">
                <div className="author-info">
                  <h4>Mustafa Fatih Yılmaz</h4>
                  <span>Avukat</span>
                </div>
              </div>
            </div>
            
            <div 
              className={`testimonial-card fade-in ${visibleElements.has('testimonial2') ? 'visible' : ''}`}
              data-animate-id="testimonial2"
            >
              <p className="testimonial-text">
                "Spor yaparken doğru beslenmenin önemini Kerem Bey'den öğrendim. Performansım arttı ve istediğim kas kütlesine ulaştım. Çok profesyonel ve ilgili bir uzman."
              </p>
              <div className="testimonial-author">
                <div className="author-info">
                  <h4>Ahmet Küçük</h4>
                  <span>Basketbol Oyuncusu</span>
                </div>
              </div>
            </div>
            
            <div 
              className={`testimonial-card fade-in ${visibleElements.has('testimonial3') ? 'visible' : ''}`}
              data-animate-id="testimonial3"
            >
              <p className="testimonial-text">
                "Uzun yıllardır spor ile iç içe olsam da Kerem Bey ile çalışmaya başladığımdan beri daha düzenli bir spor ve beslenme hayatına kavuştum. Triatlon gibi zor bir sporda ufkumu genişletti."
              </p>
              <div className="testimonial-author">
                <div className="author-info">
                  <h4>Özcan Doğan</h4>
                  <span>Triatlet</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="section contact">
        <div className="container">
          <h2 
            className={`section-title fade-in ${visibleElements.has('contact-title') ? 'visible' : ''}`}
            data-animate-id="contact-title"
          >
            İletişim
          </h2>
          <p 
            className={`section-subtitle fade-in ${visibleElements.has('contact-subtitle') ? 'visible' : ''}`}
            data-animate-id="contact-subtitle"
          >
            Sağlıklı yaşam yolculuğunuza bugün başlayın
          </p>
          
          <div className="contact-content">
            <div 
              className={`contact-info fade-in ${visibleElements.has('contact-info') ? 'visible' : ''}`}
              data-animate-id="contact-info"
            >
              <h3>Bana Ulaşın</h3>
              
              <div className="contact-item">
                <div className="contact-icon">📱</div>
                <div>
                  <h4>Telefon</h4>
                  <p>+90 531 919 7190</p>
                </div>
              </div>
              
              <div className="contact-item">
                <div className="contact-icon">✉️</div>
                <div>
                  <h4>E-posta</h4>
                  <p>dyt.kerembarbak@gmail.com</p>
                </div>
              </div>
              
              <div className="contact-item">
                <div className="contact-icon">📷</div>
                <div>
                  <h4>Instagram</h4>
                  <p>@dyt.kerembarbak</p>
                </div>
              </div>
              
            </div>
            
            <div 
              className={`contact-form fade-in ${visibleElements.has('contact-form') ? 'visible' : ''}`}
              data-animate-id="contact-form"
            >
              <h3>Randevu Talebi</h3>
              {isFormSubmitted && (
                <div className="success-message">
                  Randevu talebiniz alındı! En kısa sürede sizinle iletişime geçeceğim. 😊
                </div>
              )}
              {submitError && (
                <div className="error-message">
                  {submitError}
                </div>
              )}
              <form onSubmit={handleSubmit}>
                {/* Bot tuzağı - gerçek kullanıcılar bu alanı görmez ve doldurmaz */}
                <input
                  type="text"
                  name="website"
                  tabIndex="-1"
                  autoComplete="off"
                  aria-hidden="true"
                  value={honeypot}
                  onChange={(e) => setHoneypot(e.target.value)}
                  style={{ position: 'absolute', left: '-9999px', width: 0, height: 0, opacity: 0 }}
                />
                <div className="form-group">
                  <label htmlFor="name">Ad Soyad</label>
                  <input 
                    type="text" 
                    id="name" 
                    name="name" 
                    value={formData.name}
                    onChange={handleInputChange}
                    required 
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="email">E-posta</label>
                  <input 
                    type="email" 
                    id="email" 
                    name="email" 
                    value={formData.email}
                    onChange={handleInputChange}
                    required 
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="phone">Telefon</label>
                  <input 
                    type="tel" 
                    id="phone" 
                    name="phone" 
                    value={formData.phone}
                    onChange={handleInputChange}
                    required 
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="service">Hizmet Türü</label>
                  <select 
                    id="service" 
                    name="service" 
                    value={formData.service}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Seçiniz</option>
                    <option value="kilo-yonetimi">Kilo Yönetimi</option>
                    <option value="spor-beslenmesi">Spor Beslenmesi</option>
                    <option value="adölesan-beslenmesi">Adölesan Beslenmesi</option>
                    <option value="hastalıklarda-beslenme">Hastalıklarda Beslenme</option>
                    <option value="fitness-danışmanlıgıi">Fitness Danışmanlığı</option>
                    <option value="surdurulebilir-beslenme">Sürdürülebilir Beslenme</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label htmlFor="message">Mesajınız</label>
                  <textarea 
                    id="message" 
                    name="message" 
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="Hedefleriniz ve özel durumlarınız hakkında bilgi verebilirsiniz..."
                  />
                </div>
                
                <button
                  type="submit"
                  className="btn btn-primary"
                  style={{ width: '100%', opacity: isSubmitting ? 0.7 : 1 }}
                  disabled={isSubmitting}
                >
                  <span>📅</span> {isSubmitting ? 'Gönderiliyor...' : 'Randevu Talebini Gönder'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <p>&copy; 2024 Diyetisyen Kerem Barbak. Tüm hakları saklıdır.</p>
          <p>Sağlıklı yaşam yolculuğunuzda yanınızdayım ❤️</p>
        </div>
      </footer>
    </div>
  );
};

export default Landing;